import React, { useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import {
  genusSpeciesAutoCompleteOptions,
  gonotrophicAutoCompleteOptions,
  lifeStatusAutoCompleteOptions,
} from "../../../../../utils/const";

const SEXES = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "unsure", label: "Unsure" },
];
const FORBIDDEN = /[,/\\]/;

// "Culex pipiens" from what is stored; "Culex" alone for a genus-only ID
const identification = (c) => [c?.genus, c?.species].filter(Boolean).join(" ");

/** The genus and species of typed text: the first word, and the rest. */
export const splitIdentification = (text) => {
  const words = String(text ?? "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return { genus: "", species: "" };
  const [genus, ...rest] = words;
  return { genus: genus[0].toUpperCase() + genus.slice(1), species: rest.join(" ") };
};

const initialOf = (contributed, tags) => ({
  identification: identification(contributed),
  sex: contributed?.sex ? String(contributed.sex).toLowerCase() : null,
  lifeStage: contributed?.lifeStage ?? "",
  gonotrophic: contributed?.gonotrophic ?? "",
  tags: tags ?? [],
});

/** Only what changed, in the PATCH .../contributed shape. */
export const contributedPatch = (initial, form) => {
  const patch = {};
  if (form.identification.trim() !== initial.identification) Object.assign(patch, splitIdentification(form.identification));
  if (form.sex !== initial.sex) patch.sex = form.sex;
  if (form.lifeStage !== initial.lifeStage) patch.lifeStage = form.lifeStage || null;
  if (form.gonotrophic !== initial.gonotrophic) patch.gonotrophic = form.gonotrophic || null;
  if (form.tags.join("\u0000") !== initial.tags.join("\u0000")) patch.tags = form.tags;
  return patch;
};

/**
 * The contributed identification, editable in place: the species, sex, life
 * stage, gonotrophic state and the specimen's tags. Saves only what changed;
 * `onSave(patch, {next})` does the request.
 */
export const ContributedEditor = ({ data, onSave, canAdvance }) => {
  const { tray, specimen } = data;
  const initial = useMemo(() => initialOf(specimen.contributed, specimen.tags), [specimen]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    setForm(initial);
    setError(null);
  }, [initial]);

  const options = String(tray.vectorType ?? "").startsWith("tick")
    ? genusSpeciesAutoCompleteOptions.tick
    : genusSpeciesAutoCompleteOptions.mosquito;
  const patch = contributedPatch(initial, form);
  const dirty = Object.keys(patch).length > 0;
  const invalid =
    FORBIDDEN.test(form.identification) || form.tags.some((t) => FORBIDDEN.test(t))
      ? "Commas and slashes can't be saved: they break the exports."
      : null;
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async (next) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(patch, { next });
    } catch (err) {
      setError(err?.response?.data?.error || "The identification could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" aria-label="Contributed identification" onSubmit={(e) => e.preventDefault()} sx={{ mt: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography variant="overline" color="text.secondary" component="h3" sx={{ lineHeight: 1.5 }}>
        Contributed identification
      </Typography>
      <Autocomplete
        freeSolo
        size="small"
        options={options}
        value={form.identification}
        inputValue={form.identification}
        onInputChange={(_, v) => set("identification")(v)}
        renderInput={(params) => (
          <TextField {...params} label="Species" placeholder="Genus species, or a genus alone" helperText="A genus alone reports as Genus sp. Clear it to use the algorithm's result." />
        )}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="body2" color="text.secondary" id="contributed-sex">
          Sex
        </Typography>
        <ToggleButtonGroup exclusive size="small" value={form.sex} onChange={(_, v) => set("sex")(v)} aria-labelledby="contributed-sex">
          {SEXES.map((s) => (
            <ToggleButton key={s.value} value={s.value} sx={{ textTransform: "none", px: 1.5 }}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <TextField select size="small" label="Life stage" value={form.lifeStage} onChange={(e) => set("lifeStage")(e.target.value)} sx={{ flex: 1 }}>
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {lifeStatusAutoCompleteOptions.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Gonotrophic state" value={form.gonotrophic} onChange={(e) => set("gonotrophic")(e.target.value)} sx={{ flex: 1 }}>
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {gonotrophicAutoCompleteOptions.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Autocomplete
        multiple
        freeSolo
        size="small"
        options={[]}
        value={form.tags}
        onChange={(_, v) => set("tags")(v.map((t) => t.trim()).filter(Boolean))}
        renderInput={(params) => <TextField {...params} label="Specimen tags" placeholder={form.tags.length ? "" : "Type a tag and press Enter"} />}
      />
      {(invalid || error) && <Alert severity="error">{invalid || error}</Alert>}
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button size="small" disabled={!dirty || saving} onClick={() => setForm(initial)}>
          Undo changes
        </Button>
        <Button size="small" variant="outlined" disabled={!dirty || saving || Boolean(invalid)} onClick={() => save(false)}>
          Save
        </Button>
        {canAdvance && (
          <Button size="small" variant="contained" disableElevation disabled={!dirty || saving || Boolean(invalid)} onClick={() => save(true)}>
            Save and next
          </Button>
        )}
      </Box>
    </Box>
  );
};
